<script setup lang="ts">
import { SentenceGroupType } from 'types/types'
import { computed, onMounted, reactive, ref, watchEffect } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import GroupedSentenceGroupSelect from '@/components/GroupedSentenceGroupSelect.vue'
import { setupAutoSingleSelect } from '@/composables/useAutoSingleSelect'
import store from '@/store'
import { querySentenceGroupsLight } from '@/store/queryHelpers'

const innerSentenceGroupSelect = ref<{ focus: () => void } | null>(null)
const logger = useLogger()
const localSentenceGroups = ref<Array<SentenceGroupType>>([])

interface Props {
  clearable?: boolean
  disabled?: boolean
  externalSet?: boolean
  only?: Array<SentenceGroupType> | null
  preventAutoSingleSelect?: boolean
  remove?: Array<SentenceGroupType>
  resetSelectionOnMultiple?: boolean
  sentenceGroup: SentenceGroupType | null
}

const props = withDefaults(defineProps<Props>(), {
  clearable: true,
  disabled: false,
  externalSet: false,
  only: null,
  preventAutoSingleSelect: true,
  remove: (): Array<SentenceGroupType> => [],
  resetSelectionOnMultiple: false,
})
const emit = defineEmits(['update:sentenceGroup'])

type StateType = {
  selectedSentenceGroup: SentenceGroupType | null
}

const state = reactive<StateType>({
  selectedSentenceGroup: null,
})

onMounted(async () => {
  localSentenceGroups.value = await querySentenceGroupsLight()

  watchEffect(() => {
    if (props.externalSet) {
      state.selectedSentenceGroup = props.sentenceGroup
    }
  })

  setupAutoSingleSelect({
    filteredItems: filteredSentenceGroups,
    preventAutoSelect: () => props.preventAutoSingleSelect,
    resetOnMultiple: () => props.resetSelectionOnMultiple,
    setSelected: (item) => {
      state.selectedSentenceGroup = item
    },
  })

  watchEffect(() => {
    if (state.selectedSentenceGroup) {
      store.dispatch.student.fetchSentenceGroupSentences({
        sentenceGroup: state.selectedSentenceGroup,
      })
    }
    emit('update:sentenceGroup', state.selectedSentenceGroup)
  })
})

const filteredSentenceGroups = computed<Array<SentenceGroupType>>(() => {
  const sentenceGroups = props.only ?? localSentenceGroups.value
  logger.debug('filteredSentenceGroups, props.only =')
  logger.debug(props.only)
  logger.debug('filteredSentenceGroups, sentenceGroups =')
  logger.debug(sentenceGroups)

  const removeIds = new Set(props.remove.map((r) => r.reading.id))
  return sentenceGroups
    .filter((s: SentenceGroupType) => !removeIds.has(s.reading.id))
    .toSorted((a: SentenceGroupType, b: SentenceGroupType) => (a.reading.description ?? '').localeCompare(b.reading.description ?? ''))
})

function focus() {
  innerSentenceGroupSelect.value?.focus()
}

function clear() {
  state.selectedSentenceGroup = null
}

defineExpose({ clear, focus })
</script>

<template>
  <grouped-sentence-group-select
    ref="innerSentenceGroupSelect"
    v-model="state.selectedSentenceGroup"
    :clearable="props.clearable"
    :disabled="props.disabled"
    :label="state.selectedSentenceGroup ? 'B\'racha / Song' : 'Choose a B\'rachah / Song'"
    :loading="!filteredSentenceGroups || !filteredSentenceGroups.length"
    :sentence-groups="filteredSentenceGroups"
  />
</template>
